import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Col, Grid, RSelect } from '@common/components';
import i18n from '@common/config/i18n';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { DashboardSection, LayoutFilters } from '@dashboard/components';
import ResumeCards from '@dashboard/components/ResumeCards/ResumeCards';

import {
  selectFilter,
  shouldApplyFilters,
  applyFilters,
} from '@dashboard/slices/filters';
import { useAnalysisStatsMutation } from '@dashboard/api/analysisStats';
import {
  selectStats,
  setStats,
  setCurrentPublication,
  setCurrentAge,
} from '@dashboard/slices/analysisStats';
import { setCurrentPublication as setCurrentPublicationResults } from '@dashboard/slices/resultsStats';

import { useTranslation, Trans } from 'react-i18next';
import DateFilters from '@dashboard/components/DateFilters/DateFilters';
import NotResults from '@dashboard/components/NotResults/NotResults';
import { ChartStat } from '@dashboard/components/ChartStat';
import { AGE_WEIGHTS } from '../../constants';
import { sortCharts } from '../../util';

import viewClasses from './AnalysisStatsView.module.scss';

let lastResults = null;
let currentCall = null;

export default function AnalysisStatsView() {
  const dispatch = useAppDispatch();
  const currentStats = useAppSelector(selectStats);
  const currentFilters = useAppSelector(selectFilter);
  const publicationsT = useTranslation('dashboard\\Publications');
  const applyFiltersSignal = useAppSelector(shouldApplyFilters);

  const { t } = useTranslation('dashboard\\AnalysisStatsView');
  const [publications, setPublications] = useState([]);
  const [ages, setAges] = useState([]);

  const [selectedAge, setSelectedAge] = useState(
    currentStats.selectedAge ? currentStats.selectedAge : undefined,
  );

  const [selectedPublication, setSelectedPublication] = useState(
    currentStats.selectedPublication
      ? currentStats.selectedPublication
      : undefined,
  );

  const [analysisStats, { isLoading }] = useAnalysisStatsMutation();
  const [loading, setLoading] = useState(isLoading);
  const cancelAllRequests = () => {
    if (currentCall) currentCall.abort();
  };

  const filterAnalysis = async () => {
    if (applyFiltersSignal) {
      try {
        cancelAllRequests();
        const q = {
          ...currentFilters,
          publication: currentStats.selectedPublication,
          age: currentStats.selectedAge,
        };
        currentCall = analysisStats(q);
        lastResults = await currentCall.unwrap();
        dispatch(setStats(lastResults.data));
      } catch (ex) {
        if (ex && ex.message === 'Aborted') {
          setLoading(false);
        }
      }
    }
  };

  const handleChangePublication = (pj) => {
    Promise.all([
      dispatch(setCurrentPublication(pj.value)),
      dispatch(setCurrentPublicationResults(pj.value)),
      dispatch(applyFilters()),
    ]);
  };

  const handleChangeAge = (pj) => {
    Promise.all([dispatch(setCurrentAge(pj.value)), dispatch(applyFilters())]);
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setPublications(
      currentStats.publications
        .map((p) => ({
          label: publicationsT.t(p),
          value: p,
        }))
        .sort((p1, p2) => p1.label.localeCompare(p2.label)),
    );

    setAges(
      currentStats.ages
        .map((p) => ({ label: publicationsT.t(p), value: p }))
        .sort((c1, c2) => AGE_WEIGHTS[c1.value] - AGE_WEIGHTS[c2.value]),
    );
    if (
      !selectedPublication ||
      selectedPublication.value !== currentStats.selectedPublication
    ) {
      setSelectedPublication({
        label: publicationsT.t(currentStats.selectedPublication),
        value: currentStats.selectedPublication,
      });
    }
    setSelectedAge({
      label: publicationsT.t(currentStats.selectedAge),
      value: currentStats.selectedAge,
    });
  }, [currentStats]);

  useEffect(() => {
    filterAnalysis();
  }, [applyFiltersSignal, currentStats.selectedPublication, currentStats.selectedAge]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      if (lastResults) {
        setLoading(true);
        setTimeout(() => {
          dispatch(setStats({ ...lastResults.data }));
          setLoading(false);
        });
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useLayoutEffect(() => {
    return () => {
      cancelAllRequests();
    };
  }, []);

  return (
    <LayoutFilters>
      <section className={viewClasses.dashboard}>
        <ResumeCards resume={currentStats.resume} />
        <DashboardSection title={t('filtersTitle')}>
          <DateFilters isLoading={loading} />
          <Grid justify="start" align="middle">
            <Col xss={12} xs={12} sm={3}>
              <RSelect
                data={publications}
                val={selectedPublication}
                onChange={(e) => handleChangePublication(e)}
                placeholder={publicationsT.t('selectPublicationLabel')}
              />
            </Col>
            <Col xss={12} xs={12} sm={3}>
              <RSelect
                data={ages}
                val={selectedAge}
                onChange={(e) => handleChangeAge(e)}
                placeholder={publicationsT.t('selectAgeLabel')}
              />
            </Col>
          </Grid>
        </DashboardSection>

        {!loading ? (
          <Grid>
            <Col xss={12} xs={12} sm={12}>
              {currentStats &&
              currentStats.stats &&
              currentStats.stats.length > 0 ? (
                [...currentStats.stats]
                  .sort(sortCharts)
                  .map((stat) => (
                    <ChartStat
                      key={stat.name}
                      stat={stat}
                      title={<Trans t={t} i18nKey={stat.name} />}
                    />
                  ))
              ) : (
                <NotResults isLoading={loading} card />
              )}
            </Col>
          </Grid>
        ) : (
          <NotResults isLoading={loading} card />
        )}
      </section>
    </LayoutFilters>
  );
}

AnalysisStatsView.propTypes = {};
